import { request } from "@/api/client";
import type {
  EmailLog,
  EmailMailboxItem,
  EmailStatus,
  EmailTemplate,
  EmailThreadSummary,
  EmailThreadMessage,
  EmailThreadComment,
  SendEmailPayload,
} from "@/types/email";

export function fetchEmailTemplates() {
  return request<EmailTemplate[]>("/email/templates");
}


export function fetchEmailStatus() {
  return request<EmailStatus>("/email/status");
}

export function createEmailTemplate(payload: { name: string; subject: string; body_html: string; variant?: string | null }) {
  return request<EmailTemplate>("/email/templates", {
    method: "POST",
    body: payload,
  });
}

export function sendEmail(payload: SendEmailPayload) {
  return request<EmailLog>("/email/send", {
    method: "POST",
    body: payload,
  });
}

export function fetchEmailLogs(filters: { lead_id?: number; contact_id?: number; limit?: number; direction?: "inbound" | "outbound" | "all" } = {}) {
  const params = new URLSearchParams();
  if (filters.contact_id) params.set("contact_id", String(filters.contact_id));
  if (filters.lead_id) params.set("lead_id", String(filters.lead_id));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.direction) params.set("direction", filters.direction);
  const suffix = params.toString();
  return request<EmailLog[]>(`/email/logs${suffix ? `?${suffix}` : ""}`);
}

export function updateEmailStatus(logId: string, payload: { status: string; opened_at?: string; error_message?: string }) {
  return request<EmailLog>(`/email/status/${encodeURIComponent(logId)}`, {
    method: "PATCH",
    body: payload,
  });
}

export function fetchEmailMailbox(folder: "inbox" | "sent", params: { limit?: number } = {}) {
  const search = new URLSearchParams();
  search.set("folder", folder);
  if (params.limit) {
    search.set("limit", String(params.limit));
  }
  return request<EmailMailboxItem[]>(`/email/mailbox?${search.toString()}`);
}

export function linkEmailContact(logId: string, payload: { contact_id: number }) {
  return request<EmailLog>(`/email/logs/${encodeURIComponent(logId)}/contact`, {
    method: "POST",
    body: payload,
  });
}

export function listEmailThreads(params: {
  folder?: "inbox" | "sent" | "all";
  search?: string;
  contact_id?: number;
  lead_id?: number;
  limit?: number;
} = {}) {
  const search = new URLSearchParams();
  if (params.folder) search.set("folder", params.folder);
  if (params.search) search.set("search", params.search);
  if (params.contact_id) search.set("contact_id", String(params.contact_id));
  if (params.lead_id) search.set("lead_id", String(params.lead_id));
  if (params.limit) search.set("limit", String(params.limit));
  const suffix = search.toString();
  return request<EmailThreadSummary[]>(`/email/threads${suffix ? `?${suffix}` : ""}`);
}

export function listThreadMessages(threadId: string) {
  return request<EmailThreadMessage[]>(`/email/threads/${encodeURIComponent(threadId)}/messages`);
}

export function listEmailThreadComments(threadId: string) {
  return request<EmailThreadComment[]>(`/email/threads/${encodeURIComponent(threadId)}/comments`);
}

export function createEmailThreadComment(threadId: string, payload: { text: string }) {
  return request<EmailThreadComment>(`/email/threads/${encodeURIComponent(threadId)}/comments`, {
    method: "POST",
    body: payload,
  });
}

export function dismissEmailThread(threadId: string) {
  return request<void>(`/email/threads/${encodeURIComponent(threadId)}/dismiss`, {
    method: "POST",
  });
}
